import React, { useEffect, useState } from 'react';
import { Button } from './ui/button';
import { Download, X, Smartphone } from 'lucide-react';

const STORAGE_KEY = 'rw_pwa_install_dismissed_at';
const DISMISS_COOLDOWN_DAYS = 14;

function isStandalone() {
  if (typeof window === 'undefined') return false;
  const mq = window.matchMedia && window.matchMedia('(display-mode: standalone)').matches;
  // iOS Safari uses navigator.standalone
  return !!(mq || window.navigator?.standalone);
}

function wasRecentlyDismissed() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return false;
    const at = parseInt(raw, 10);
    if (!at) return false;
    const days = (Date.now() - at) / (1000 * 60 * 60 * 24);
    return days < DISMISS_COOLDOWN_DAYS;
  } catch {
    return false;
  }
}

/**
 * One-time "Add to Home Screen" prompt for providers using Field View on mobile.
 *
 * - Captures the browser's `beforeinstallprompt` event (Chrome/Edge/Android)
 * - Falls back to an iOS Safari instruction card (no native prompt available)
 * - Remembers dismissal for 14 days
 * - Hidden when already running as installed PWA
 */
export default function InstallPwaPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [show, setShow] = useState(false);
  const [iosTip, setIosTip] = useState(false);

  useEffect(() => {
    if (isStandalone()) return;
    if (wasRecentlyDismissed()) return;

    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShow(true);
    };
    window.addEventListener('beforeinstallprompt', handler);

    // iOS Safari: no `beforeinstallprompt`. Detect and show a manual tip.
    const ua = window.navigator?.userAgent || '';
    const isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
    const isSafari = /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS/.test(ua);
    if (isIOS && isSafari) {
      setIosTip(true);
      setShow(true);
    }

    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const dismiss = () => {
    try {
      localStorage.setItem(STORAGE_KEY, String(Date.now()));
    } catch {
      // ignore — non-critical
    }
    setShow(false);
  };

  const install = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    try {
      await deferredPrompt.userChoice;
    } catch {
      // ignore
    }
    setDeferredPrompt(null);
    setShow(false);
  };

  if (!show) return null;

  return (
    <div
      className="mb-3 rounded-lg border bg-primary/5 border-primary/20 p-3 flex items-start gap-3"
      data-testid="pwa-install-prompt"
    >
      <Smartphone className="h-5 w-5 text-primary mt-0.5 shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-foreground">
          Add RanchWorks to your home screen
        </p>
        {iosTip ? (
          <p className="text-xs text-muted-foreground mt-0.5">
            Tap <span className="font-medium">Share</span> &rarr;{' '}
            <span className="font-medium">Add to Home Screen</span> for one-tap
            access in the field.
          </p>
        ) : (
          <p className="text-xs text-muted-foreground mt-0.5">
            Install the app for one-tap access, offline support, and a
            full-screen field workspace.
          </p>
        )}
        <div className="flex flex-wrap gap-2 mt-2">
          {!iosTip && deferredPrompt && (
            <Button
              size="sm"
              onClick={install}
              data-testid="pwa-install-btn"
              className="h-8"
            >
              <Download className="h-3 w-3 mr-1" /> Install
            </Button>
          )}
          <Button
            size="sm"
            variant="ghost"
            onClick={dismiss}
            data-testid="pwa-install-dismiss"
            className="h-8"
          >
            Not now
          </Button>
        </div>
      </div>
      <button
        type="button"
        onClick={dismiss}
        className="p-1 text-muted-foreground hover:text-foreground"
        aria-label="Close"
        data-testid="pwa-install-close"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}
